import React, { useState, useEffect } from 'react';
import { DollarSign, UserCheck, RefreshCw, Calendar, CreditCard, Loader, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import {
  getReferrersWithUnpaidCommission,
  recordCommissionPayment,
  getPaymentHistory
} from '../api/referrers';
import LoadingSpinner from '../components/common/LoadingSpinner';

const CommissionPaymentsPage = () => {
  const { user } = useAuth();
  const [referrers, setReferrers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReferrer, setSelectedReferrer] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [paymentData, setPaymentData] = useState({
    amount: '',
    paymentMethod: 'cash',
    paymentReference: '',
    notes: ''
  });

  useEffect(() => {
    fetchReferrers();
  }, []);

  const fetchReferrers = async () => {
    setLoading(true);
    try {
      const data = await getReferrersWithUnpaidCommission();
      setReferrers(data);
    } catch (error) {
      toast.error('Failed to fetch referrers');
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentHistory = async (referrerId) => {
    setLoadingHistory(true);
    try {
      const data = await getPaymentHistory(referrerId);
      setPaymentHistory(data.payments || []);
    } catch (error) {
      toast.error('Failed to fetch payment history');
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleShowPaymentModal = (referrer) => {
    setSelectedReferrer(referrer);
    setPaymentData({
      amount: referrer.commission_balance,
      paymentMethod: 'cash',
      paymentReference: '',
      notes: ''
    });
    setShowPaymentModal(true);
  };

  const handleShowHistory = async (referrer) => {
    setSelectedReferrer(referrer);
    setShowHistoryModal(true);
    await fetchPaymentHistory(referrer.id);
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();

    if (parseFloat(paymentData.amount) <= 0) {
      toast.error('Payment amount must be greater than zero');
      return;
    }

    if (parseFloat(paymentData.amount) > selectedReferrer.commission_balance) {
      toast.error('Payment amount exceeds commission balance');
      return;
    }

    setSubmitting(true);
    try {
      await recordCommissionPayment(selectedReferrer.id, {
        amount: parseFloat(paymentData.amount),
        paymentMethod: paymentData.paymentMethod,
        paymentReference: paymentData.paymentReference,
        notes: paymentData.notes
      });

      toast.success('Payment recorded successfully');
      setShowPaymentModal(false);
      fetchReferrers();
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to record payment';
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const canEdit = ['admin', 'super_admin', 'system_admin', 'branch_admin', 'retail_admin'].includes(
    user?.role
  );

  if (!canEdit) {
    return (
      <div className="min-h-screen bg-theme-primary text-theme-primary flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-2">Access Denied</h2>
          <p className="text-theme-secondary">You don't have permission to access this page</p>
        </div>
      </div>
    );
  }

  const totalUnpaidCommissions = referrers.reduce(
    (sum, ref) => sum + (ref.commission_balance || 0),
    0
  );

  return (
    <div className="min-h-screen bg-theme-primary text-theme-primary">
      <div className="max-w-7xl mx-auto p-4 md:p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-theme-primary">
                Commission Payments
              </h1>
              <p className="text-sm md:text-base text-theme-secondary mt-1">
                Manage and record commission payments
              </p>
            </div>
            <button
              onClick={fetchReferrers}
              className="flex items-center gap-2 px-4 py-2 bg-theme-secondary text-theme-primary border border-theme rounded-lg hover:bg-theme-tertiary transition-colors"
            >
              <RefreshCw className="w-5 h-5" />
              Refresh
            </button>
          </div>

          {/* Summary Card */}
          <div className="mt-6 bg-theme-secondary rounded-lg shadow-theme-md p-6 border border-theme">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-theme-secondary">
                  Total Unpaid Commissions
                </h2>
                <p className="text-3xl font-bold text-amber-500 mt-2">
                  {formatCurrency(totalUnpaidCommissions)}
                </p>
              </div>
              <DollarSign className="w-16 h-16 text-amber-500 opacity-50" />
            </div>
            <p className="text-sm text-theme-muted mt-4">
              {referrers.length} referrer{referrers.length !== 1 ? 's' : ''} with pending payments
            </p>
          </div>
        </div>

        {/* Referrers List */}
        {loading ? (
          <LoadingSpinner message="Loading commission balances..." className="py-20" />
        ) : referrers.length === 0 ? (
          <div className="bg-theme-secondary border border-theme rounded-lg p-12 text-center">
            <UserCheck className="w-16 h-16 text-theme-muted mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-theme-primary mb-2">No Pending Payments</h3>
            <p className="text-theme-secondary">All referrer commissions have been paid!</p>
          </div>
        ) : (
          <div className="bg-theme-secondary border border-theme rounded-lg shadow-theme-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-theme-tertiary border-b border-theme">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-theme-muted uppercase tracking-wider">
                      Referrer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-theme-muted uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-theme-muted uppercase tracking-wider">
                      Total Earned
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-theme-muted uppercase tracking-wider">
                      Total Paid
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-theme-muted uppercase tracking-wider">
                      Balance Due
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-theme-muted uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-theme-secondary divide-y divide-theme">
                  {referrers.map((referrer) => (
                    <tr key={referrer.id} className="hover:bg-theme-tertiary transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-theme-primary">
                            {referrer.full_name}
                          </div>
                          <div className="text-sm text-theme-secondary">
                            {referrer.referrer_code}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-theme-primary">{referrer.phone}</div>
                        {referrer.email && (
                          <div className="text-sm text-theme-secondary">{referrer.email}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-green-600 dark:text-green-400">
                          {formatCurrency(referrer.total_commission_earned)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-theme-primary">
                          {formatCurrency(referrer.total_commission_paid)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-bold text-amber-500">
                          {formatCurrency(referrer.commission_balance)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleShowPaymentModal(referrer)}
                            className="px-3 py-1 bg-accent-gold text-primary-black font-semibold rounded hover:opacity-90 transition-all"
                          >
                            Pay Now
                          </button>
                          <button
                            onClick={() => handleShowHistory(referrer)}
                            className="px-3 py-1 border border-theme text-theme-secondary bg-theme-tertiary rounded hover:bg-theme-secondary transition-colors"
                          >
                            History
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Payment Modal */}
        {showPaymentModal && selectedReferrer && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-theme-secondary border border-theme rounded-lg shadow-theme-xl max-w-md w-full">
              <div className="flex items-center justify-between p-6 border-b border-theme">
                <h3 className="text-xl font-bold text-theme-primary">Record Payment</h3>
                <button
                  onClick={() => setShowPaymentModal(false)}
                  className="text-theme-muted hover:text-theme-secondary"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handlePaymentSubmit} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-theme-secondary mb-1">
                    Referrer
                  </label>
                  <div className="p-3 bg-theme-tertiary border border-theme rounded-lg">
                    <p className="font-semibold text-theme-primary">{selectedReferrer.full_name}</p>
                    <p className="text-sm text-theme-secondary">{selectedReferrer.referrer_code}</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-theme-secondary mb-1">
                    Commission Balance
                  </label>
                  <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <p className="text-2xl font-bold text-amber-500">
                      {formatCurrency(selectedReferrer.commission_balance)}
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-theme-secondary mb-1">
                    Payment Amount <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={paymentData.amount}
                    onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                    min="0"
                    step="0.01"
                    required
                    className="w-full px-4 py-2 bg-theme-tertiary border border-theme rounded-lg text-theme-primary focus:ring-2 focus:ring-accent-gold focus:border-transparent"
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-theme-secondary mb-1">
                    Payment Method
                  </label>
                  <select
                    value={paymentData.paymentMethod}
                    onChange={(e) =>
                      setPaymentData({ ...paymentData, paymentMethod: e.target.value })
                    }
                    className="w-full px-4 py-2 bg-theme-tertiary border border-theme rounded-lg text-theme-primary focus:ring-2 focus:ring-accent-gold focus:border-transparent"
                  >
                    <option value="cash">Cash</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="cheque">Cheque</option>
                    <option value="mobile_money">Mobile Money</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-theme-secondary mb-1">
                    Payment Reference (Optional)
                  </label>
                  <input
                    type="text"
                    value={paymentData.paymentReference}
                    onChange={(e) =>
                      setPaymentData({ ...paymentData, paymentReference: e.target.value })
                    }
                    className="w-full px-4 py-2 bg-theme-tertiary border border-theme rounded-lg text-theme-primary focus:ring-2 focus:ring-accent-gold focus:border-transparent"
                    placeholder="Transaction ID, Cheque No., etc."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-theme-secondary mb-1">
                    Notes (Optional)
                  </label>
                  <textarea
                    value={paymentData.notes}
                    onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                    rows="3"
                    className="w-full px-4 py-2 bg-theme-tertiary border border-theme rounded-lg text-theme-primary focus:ring-2 focus:ring-accent-gold focus:border-transparent"
                    placeholder="Add any additional notes..."
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowPaymentModal(false)}
                    className="flex-1 px-4 py-2 border border-theme bg-theme-tertiary rounded-lg text-theme-secondary font-semibold hover:bg-theme-secondary transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 px-4 py-2 bg-accent-gold text-primary-black font-semibold rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {submitting ? (
                      <>
                        <Loader className="w-5 h-5 animate-spin" />
                        Recording...
                      </>
                    ) : (
                      'Record Payment'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Payment History Modal */}
        {showHistoryModal && selectedReferrer && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-theme-secondary border border-theme rounded-lg shadow-theme-xl max-w-4xl w-full max-h-[80vh] overflow-hidden flex flex-col">
              <div className="flex items-center justify-between p-6 border-b border-theme">
                <h3 className="text-xl font-bold text-theme-primary">
                  Payment History - {selectedReferrer.full_name}
                </h3>
                <button
                  onClick={() => setShowHistoryModal(false)}
                  className="text-theme-muted hover:text-theme-secondary"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6">
                {loadingHistory ? (
                  <LoadingSpinner message="Loading history..." className="py-12" />
                ) : paymentHistory.length === 0 ? (
                  <div className="text-center py-12">
                    <Calendar className="w-16 h-16 text-theme-muted mx-auto mb-4" />
                    <p className="text-theme-secondary">No payment history found</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {paymentHistory.map((payment) => (
                      <div
                        key={payment.id}
                        className="bg-theme-secondary border border-theme rounded-lg p-4 shadow-theme-sm"
                      >
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                              {formatCurrency(payment.amount)}
                            </p>
                            <p className="text-sm text-theme-secondary mt-1">
                              {formatDate(payment.paid_at)}
                            </p>
                          </div>
                          <div className="text-right">
                            <span className="inline-block px-3 py-1 bg-blue-500/10 text-blue-500 border border-blue-500/20 text-xs font-semibold rounded">
                              {payment.payment_method.replace('_', ' ').toUpperCase()}
                            </span>
                          </div>
                        </div>

                        {payment.payment_reference && (
                          <div className="mb-2">
                            <label className="text-xs text-theme-muted">Reference:</label>
                            <p className="text-sm font-medium text-theme-primary">
                              {payment.payment_reference}
                            </p>
                          </div>
                        )}

                        {payment.notes && (
                          <div className="mb-2">
                            <label className="text-xs text-theme-muted">Notes:</label>
                            <p className="text-sm text-theme-secondary">{payment.notes}</p>
                          </div>
                        )}

                        {payment.paid_by_name && (
                          <div className="text-xs text-theme-muted mt-2">
                            Paid by: {payment.paid_by_name}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommissionPaymentsPage;
